/* eslint-env es2020 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Typography,
  message,
  Descriptions,
} from 'antd';
import { EyeOutlined, PlusOutlined, MinusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { walletAPI } from '../api';
import { normalizeMoney } from '../utils/money';

const { Option } = Select;
const { Title, Text } = Typography;

const reasonOptions = [
  // 可选交易原因（用于手工调账弹窗）
  { value: 'deposit', label: '充值' },
  { value: 'refund', label: '退款' },
  { value: 'withdrawal', label: '提现扣减' },
  { value: 'adjustment', label: '手动调整' },
  { value: 'chargeback', label: '拒付' },
  { value: 'transfer', label: '转账' },
  { value: 'purchase', label: '下单扣款' },
];

const defaultPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
};

const txPaginationTemplate = {
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条流水`,
};

const FinancialManagement = () => {
  // Admin 财务总览：支持分页查看钱包、手工调账、查看流水
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchKeyword, setSearchKeyword] = useState('');

  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjustForm] = Form.useForm();
  const [adjustType, setAdjustType] = useState('credit');
  const [currentWallet, setCurrentWallet] = useState(null);
  const [submittingAdjust, setSubmittingAdjust] = useState(false);

  const [txModalVisible, setTxModalVisible] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txPagination, setTxPagination] = useState(txPaginationTemplate);
  const [txLoading, setTxLoading] = useState(false);
  const [txWallet, setTxWallet] = useState(null);

  const fetchWallets = useCallback(async (page = pagination.current, pageSize = pagination.pageSize, keyword = searchKeyword) => {
    // 拉取钱包分页列表，支持用户名/邮箱模糊搜索
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (keyword && keyword.trim()) {
        params.search = keyword.trim();
      }
      const response = await walletAPI.listWallets(params);
      const payload = response.data;
      setWallets(payload.data || []);
      setPagination(prev => ({
        ...prev,
        current: payload.page,
        pageSize: payload.page_size,
        total: payload.total,
      }));
    } catch (error) {
      
      message.error('获取钱包列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchKeyword]);

  useEffect(() => {
    fetchWallets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchWallets(current, pageSize);
  };

  const resetPagination = () => {
    setPagination({ ...defaultPagination });
  };

  const handleSearch = () => {
    resetPagination();
    fetchWallets(1, pagination.pageSize, searchKeyword);
  };

  const handleResetSearch = () => {
    setSearchKeyword('');
    resetPagination();
    fetchWallets(1, pagination.pageSize, '');
  };

  const openAdjustModal = (wallet, type) => {
    // 打开加款/扣款弹窗前重置表单并设置默认原因
    setAdjustType(type);
    setCurrentWallet(wallet);
    adjustForm.resetFields();
    adjustForm.setFieldsValue({ reason: 'adjustment' });
    setAdjustModalVisible(true);
  };

  const submitAdjust = async () => {
    // 提交手工调账请求，金额始终转为字符串表示的两位小数
    try {
      if (!currentWallet) return;
      await adjustForm.validateFields();
      const values = adjustForm.getFieldsValue();
      setSubmittingAdjust(true);

      const payload = {
        amount_minor: normalizeMoney(values.amount_minor || '0'),
        direction: adjustType,
        reason: values.reason,
        comment: values.comment || '',
        idempotency_key: `manual-${currentWallet.id}-${adjustType}-${Date.now()}`,
        status: 'posted',
      };

      await walletAPI.createWalletTransaction(currentWallet.id, payload);
      message.success(adjustType === 'credit' ? '加款成功' : '扣款成功');
      setAdjustModalVisible(false);
      fetchWallets(pagination.current, pagination.pageSize, searchKeyword);
    } catch (error) {
      const detail = error?.response?.data?.detail; // 统一解析后端自定义错误码
      if (detail === 'INSUFFICIENT_BALANCE' || detail === 'Insufficient wallet balance') {
        message.error('扣款失败：余额不足');
      } else if (detail === 'IDEMPOTENCY_CONFLICT') {
        message.warning('重复请求已忽略');
      } else {
        message.error('操作失败，请稍后再试');
      }
      
    } finally {
      setSubmittingAdjust(false);
    }
  };

  const openTransactionsModal = (wallet) => {
    // 查看流水时刷新分页并弹出详情表格
    setTxWallet(wallet);
    setTxPagination({ ...txPaginationTemplate });
    setTxModalVisible(true);
    fetchTransactions(wallet.id, 1, txPaginationTemplate.pageSize);
  };

  const fetchTransactions = async (walletId, page = 1, pageSize = txPagination.pageSize) => {
    // 拉取指定钱包的流水列表
    try {
      setTxLoading(true);
      const response = await walletAPI.getWalletTransactions(walletId, { page, page_size: pageSize });
      const payload = response.data;
      setTransactions(payload.data || []);
      setTxPagination(prev => ({
        ...prev,
        current: payload.page,
        pageSize: payload.page_size,
        total: payload.total,
      }));
    } catch (error) {
      
      message.error('获取钱包流水失败');
    } finally {
      setTxLoading(false);
    }
  };

  const handleTxTableChange = (paginationConfig) => {
    if (!txWallet) return;
    const { current, pageSize } = paginationConfig;
    fetchTransactions(txWallet.id, current, pageSize);
  };

  const renderAmount = (record) => {
    const amount = normalizeMoney(record?.amount_minor ?? '0');
    const display = `${record?.currency || 'USD'} ${amount}`;
    if (record.direction === 'debit') {
      return <Text type="danger">-{display}</Text>;
    }
    return <Text type="success">+{display}</Text>;
  };

  const walletColumns = [
    {
      title: '用户名',
      dataIndex: ['user', 'username'],
      key: 'username',
      render: (value) => value || '—',
    },
    {
      title: '邮箱',
      dataIndex: ['user', 'email'],
      key: 'email',
      render: (value) => value || '—',
    },
    {
      title: '货币',
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
    },
    {
      title: '余额',
      dataIndex: 'balance_minor',
      key: 'balance_minor',
      render: (value, record) => `${record.currency || 'USD'} ${normalizeMoney(value || '0')}`,
    },
    {
      title: '冻结金额',
      dataIndex: 'pending_minor',
      key: 'pending_minor',
      render: (value, record) => `${record.currency || 'USD'} ${normalizeMoney(value || '0')}`,
    },
    {
      title: '最近更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<PlusOutlined />} onClick={() => openAdjustModal(record, 'credit')}>
            手动加款
          </Button>
          <Button type="link" danger icon={<MinusOutlined />} onClick={() => openAdjustModal(record, 'debit')}>
            手动扣款
          </Button>
          <Button type="link" icon={<EyeOutlined />} onClick={() => openTransactionsModal(record)}>
            查看流水
          </Button>
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—'),
      width: 180,
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      render: (value) => (
        <Tag color={value === 'credit' ? 'green' : 'volcano'}>
          {value === 'credit' ? '入账' : '出账'}
        </Tag>
      ),
    },
    {
      title: '金额',
      key: 'amount_minor',
      render: (_, record) => renderAmount(record),
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => {
        const mapping = {
          deposit: '充值',
          purchase: '下单扣款',
          refund: '退款',
          withdrawal: '提现',
          adjustment: '手动调整',
          chargeback: '拒付',
          transfer: '转账',
        };
        return mapping[value] || value;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const mapping = {
          posted: { text: '已入账', color: 'green' },
          pending: { text: '待处理', color: 'processing' },
          voided: { text: '已作废', color: 'default' },
          failed: { text: '失败', color: 'red' },
        };
        const info = mapping[value] || { text: value, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '余额快照',
      dataIndex: 'running_balance_minor',
      key: 'running_balance_minor',
      render: (value, record) => {
        if (value == null) return '—';
        return `${record.currency || 'USD'} ${normalizeMoney(value)}`;
      },
    },
    {
      title: '备注',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      width: 160,
      render: (value) => value || '—',
    },
    {
      title: '幂等键',
      dataIndex: 'idempotency_key',
      key: 'idempotency_key',
      ellipsis: true,
      width: 200,
      render: (value) => value || '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>财务管理</Title>
        <Space>
          <Input
            placeholder="搜索用户名或邮箱"
            allowClear
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 240 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>搜索</Button>
          <Button onClick={handleResetSearch} icon={<ReloadOutlined />}>重置</Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={walletColumns}
        dataSource={wallets}
        pagination={pagination}
        onChange={handleTableChange}
      />

      <Modal
        title={adjustType === 'credit' ? '手动加款' : '手动扣款'}
        open={adjustModalVisible}
        onCancel={() => setAdjustModalVisible(false)}
        onOk={submitAdjust}
        confirmLoading={submittingAdjust}
        okText={adjustType === 'credit' ? '确认加款' : '确认扣款'}
        cancelText="取消"
      >
        {currentWallet && (
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="用户">{currentWallet.user?.username || '—'}</Descriptions.Item>
            <Descriptions.Item label="当前余额">
              {`${currentWallet.currency} ${normalizeMoney(currentWallet.balance_minor || '0')}`}
            </Descriptions.Item>
          </Descriptions>
        )}
        <Form layout="vertical" form={adjustForm}>
          <Form.Item
            name="amount_minor"
            label="金额"
            rules={[
              { required: true, message: '请输入金额' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null) return Promise.resolve();
                  if (Number(value) <= 0) {
                    return Promise.reject(new Error('金额必须大于0'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0.01}
              precision={2}
              style={{ width: '100%' }}
              addonAfter={currentWallet?.currency || 'USD'}
            />
          </Form.Item>
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: '请选择原因' }]}>
            <Select>
              {reasonOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="comment" label="备注">
            <Input.TextArea rows={3} maxLength={200} placeholder="可选备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        width={900}
        title="钱包流水"
        open={txModalVisible}
        onCancel={() => setTxModalVisible(false)}
        footer={null}
      >
        {txWallet && (
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="用户">{txWallet.user?.username || '—'}</Descriptions.Item>
            <Descriptions.Item label="余额">{`${txWallet.currency} ${normalizeMoney(txWallet.balance_minor || '0')}`}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{txWallet.user?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="冻结金额">{`${txWallet.currency} ${normalizeMoney(txWallet.pending_minor || '0')}`}</Descriptions.Item>
          </Descriptions>
        )}
        <Table
          rowKey="id"
          loading={txLoading}
          columns={transactionColumns}
          dataSource={transactions}
          pagination={txPagination}
          onChange={handleTxTableChange}
        />
      </Modal>
    </div>
  );
};

export default FinancialManagement;
